import type { ApiClient } from "@pokifolio/api-client";
import { type MeResponse, type Permission, hasPermission } from "@pokifolio/contracts";
import { queryOptions, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";

import { useApi } from "../../app/api-context";

export const ME_QUERY_KEY = ["me"] as const;

/** Who is signed in (null when nobody). Cached for a minute; refreshed after sign-in/out. */
export const meQueryOptions = (api: ApiClient) =>
  queryOptions({ queryKey: ME_QUERY_KEY, queryFn: () => api.me(), staleTime: 60_000 });

/** The signed-in user. Only used inside screens that the router already protects. */
export function useCurrentUser(): MeResponse {
  const api = useApi();
  const { data } = useSuspenseQuery(meQueryOptions(api));
  if (data === null) throw new Error("useCurrentUser used outside a signed-in screen");
  return data;
}

export function useHasPermission(permission: Permission): boolean {
  return hasPermission(useCurrentUser().permissions, permission);
}

/** Forgets everything cached for the previous user (used on sign-in and sign-out). */
export function useResetSession(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    queryClient.clear();
    await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
  };
}
